import { colors, fontWeight, radius, space } from '@ardoq/design-tokens';
import { IconSize, IconName, MinimalIconProps } from '@ardoq/icons';
import {
  themeQuartz,
  iconSetMaterial,
  iconOverrides,
  type Theme,
} from 'ag-grid-community';

export const DEFAULT_ROW_HEIGHT = 42;

/**
 * Build a self-contained <svg> string by copying a sprite symbol's body from
 * the document-embedded SVG sprite. Used to feed AG Grid's iconOverrides,
 * which encodes icons as mask-image data URIs and therefore can't resolve
 * <use href="#sprite-id"> back into the document at render time.
 */
const inlineSpriteSymbol = (symbolId: string): string => {
  const symbol =
    typeof document === 'undefined' ? null : document.getElementById(symbolId);
  const inner = symbol?.innerHTML ?? '';
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">${inner}</svg>`;
};

/**
 * Build an inline <svg> string for an {@link IconName} suitable for AG Grid's
 * `MenuItemDef.icon`, which accepts an HTML string. `fill="currentColor"` lets
 * the icon inherit the menu item's text color (e.g. the red applied to a
 * destructive item via its `color`).
 */
export const buildMenuItemIconSvg = (
  iconName: MinimalIconProps['name']
): string => {
  const symbol =
    typeof document === 'undefined' || !iconName
      ? null
      : document.getElementById(iconName);
  const inner = symbol?.innerHTML ?? '';
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="${IconSize.MEDIUM}" height="${IconSize.MEDIUM}" fill="currentColor" aria-hidden="true" focusable="false" style="display:block">${inner}</svg>`;
};

const gripIconOverride = iconOverrides({
  type: 'image',
  mask: true,
  icons: {
    grip: { svg: inlineSpriteSymbol(IconName.DRAG_INDICATOR) },
  },
});

export const theme: Theme = themeQuartz
  .withParams({
    // Table container
    backgroundColor: colors.surfaceDefault,
    borderColor: colors.borderSubtle,
    wrapperBorder: {
      width: '0',
    },
    wrapperBorderRadius: radius.medium,
    fontFamily: 'inherit',
    accentColor: colors.borderFocus,
    textColor: colors.textDefault,
    headerTextColor: colors.textDefault,

    // Header
    headerHeight: 48,
    headerBackgroundColor: colors.surfaceDefault,
    headerCellHoverBackgroundColor: colors.surfaceHover,
    headerFontWeight: fontWeight.semibold,

    // Buttons
    iconButtonHoverBackgroundColor: colors.surfaceActionGhostHover,
    iconButtonActiveBackgroundColor: colors.surfaceActionGhostPressed,
    // Since ag-grid applies a large box shadow, we need to reduce the border radius to roughly match 4px
    iconButtonBorderRadius: '2px',

    // Rows
    rowHeight: DEFAULT_ROW_HEIGHT,
    selectedRowBackgroundColor: colors.surfaceActive,
    rowHoverColor: colors.surfaceHover,

    checkboxCheckedBorderColor: colors.iconActionActive,
    checkboxCheckedBackgroundColor: colors.iconActionActive,
    checkboxIndeterminateBorderColor: colors.iconAction,
    checkboxIndeterminateBackgroundColor: colors.iconActionActive,
    checkboxUncheckedBorderColor: colors.iconAction,
    checkboxBorderWidth: '1.5px',
    checkboxBorderRadius: '2px',

    spacing: space.small,
    iconColor: colors.icon,
    iconSize: IconSize.MEDIUM,
  })
  .withPart(iconSetMaterial)
  .withPart(gripIconOverride);
